import { useQuery } from '@tanstack/react-query';
import { BROKER_REQUEST_LABELS, BROKER_REQUEST_STATUSES, VIEWING_LABELS } from '@wasity/shared';
import { Bar, BarChart, Card, ErrorNotice, PageHeader, Skeleton, Sparkline, Stat, Table } from '@/components/ui';
import { useI18n } from '@/i18n';
import { api } from '@/lib/api';
import { dateOnly, money } from '@/lib/format';
import type { Analytics } from '@/lib/types';
import { dailySeries } from './common';

export function AdminAnalytics() {
  const { t, locale, pick } = useI18n();
  const a = useQuery({ queryKey: ['admin', 'analytics'], queryFn: () => api.get<Analytics>('/api/admin/analytics') });
  if (a.isLoading) return <Skeleton lines={8} />;
  if (a.error || !a.data) return <ErrorNotice error={a.error} retry={() => void a.refetch()} labels={{ message: t('app.error'), retry: t('app.retry') }} />;
  const d = a.data;
  const n = (rows: { status: string; n: number }[], s: string) => rows.find((r) => r.status === s)?.n ?? 0;
  const total = d.brokerRequests.byStatus.reduce((s, r) => s + r.n, 0);
  const completed = n(d.brokerRequests.byStatus, 'completed');
  const revenue = d.revenue30d.reduce((s, r) => s + r.halalas, 0);
  const br30 = dailySeries(d.brokerRequests.trend, 30);
  const ev30 = dailySeries(d.evaluations.trend, 30);
  const revenueRows = [
    { label: t('admin.analytics.confirmedCommissions'), row: d.revenue30d.find((r) => r.kind === 'commission') },
    { label: t('admin.analytics.evaluationFees'), row: d.revenue30d.find((r) => r.kind === 'evaluation_fee') },
    { label: t('admin.analytics.memberships'), row: d.revenue30d.find((r) => r.kind === 'membership') },
  ];
  return (
    <div className="flex flex-col gap-6">
      <PageHeader crumbs={[{ to: '/admin', label: t('nav.dashboard') }, { label: t('nav.analytics') }]} title={t('nav.analytics')} meta={`${t('admin.analytics.last30')} · ${dateOnly(d.since, locale)}`} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label={t('admin.analytics.brokerRequests')} value={br30.reduce((s, x) => s + x, 0)} hint={`${t('nav.evaluations')}: ${ev30.reduce((s, x) => s + x, 0)}`} />
        <Stat label={t('admin.analytics.completed')} value={completed} hint={t('admin.analytics.completionRate', { p: total ? Math.round((completed / total) * 100) : 0 })} />
        <Stat label={t('admin.analytics.revenue')} value={money(revenue, locale)} hint={t('admin.analytics.revenueHint')} />
        <Stat label={t('admin.analytics.avgAcceptance')} value={t('admin.analytics.minutesN', { n: d.avgAcceptanceMinutes })} hint={t('admin.analytics.cancelledN', { n: n(d.brokerRequests.byStatus, 'cancelled') })} />
      </div>
      <div className="grid lg:grid-cols-2 gap-3">
        <Card>
          <h2 className="mb-3">{t('admin.analytics.byStatus')}</h2>
          <BarChart data={BROKER_REQUEST_STATUSES.map((s) => ({ label: pick(BROKER_REQUEST_LABELS[s]), value: n(d.brokerRequests.byStatus, s), tone: s === 'completed' ? 'ink' : s === 'cancelled' ? 'muted' : 'accent' }))} />
        </Card>
        <Card>
          <h2 className="mb-3">{t('admin.analytics.daily')}</h2>
          <Sparkline height={200} series={[{ points: br30, area: true }, { points: ev30, color: 'var(--accent-2)' }]} labels={{ start: dateOnly(d.since, locale), end: dateOnly(new Date(), locale) }} />
          <p className="flex gap-4 text-[13px] text-muted mt-2"><span><i className="inline-block size-2.5 rounded-sm bg-accent me-1.5 align-middle" />{t('admin.analytics.brokerRequests')}</span><span><i className="inline-block size-2.5 rounded-sm bg-accent-2 me-1.5 align-middle" />{t('nav.evaluations')}</span></p>
        </Card>
      </div>
      <div className="grid lg:grid-cols-2 gap-3">
        <Card>
          <h2 className="mb-3">{t('admin.analytics.revenue30')}</h2>
          <Table>
            <thead><tr><th>{t('app.kind')}</th><th>{t('app.count')}</th><th>{t('app.amount')}</th><th className="w-[40%]"></th></tr></thead>
            <tbody>
              {revenueRows.map((r) => (
                <tr key={r.label}><td>{r.label}</td><td className="num">{r.row?.n ?? 0}</td><td className="num">{money(r.row?.halalas ?? 0, locale)}</td><td><Bar value={r.row?.halalas ?? 0} max={revenue || 1} /></td></tr>
              ))}
            </tbody>
          </Table>
        </Card>
        <Card>
          <h2 className="mb-3">{t('admin.analytics.brokerUsage')}</h2>
          {d.brokerUsage.length ? (
            <Table>
              <thead><tr><th>{t('app.broker')}</th><th>{t('admin.analytics.tier')}</th><th>{t('app.slots')}</th><th className="w-[35%]"></th><th>{t('admin.analytics.completed')}</th></tr></thead>
              <tbody>
                {d.brokerUsage.map((b) => (
                  <tr key={b.userId}><td>{b.name}</td><td>{b.tier ? (locale === 'ar' ? b.tier.nameAr : b.tier.nameEn) : '—'}</td><td className="num">{b.used} / {b.max}</td><td><Bar value={b.used} max={b.max} /></td><td className="num">{b.completed}</td></tr>
                ))}
              </tbody>
            </Table>
          ) : <p className="text-muted text-[14px]">{t('app.none')}</p>}
        </Card>
      </div>
      <Card>
        <h2 className="mb-3">{t('admin.analytics.viewingsByStatus')}</h2>
        <div className="flex flex-wrap gap-4 text-[14px]">
          {d.viewings.byStatus.map((v) => <span key={v.status}>{pick(VIEWING_LABELS[v.status])} · <b className="num">{v.n}</b></span>)}
          {!d.viewings.byStatus.length ? <span className="text-muted">{t('app.none')}</span> : null}
        </div>
      </Card>
    </div>
  );
}
